import React, { useState, useContext } from 'react';
import { useNotifications } from '../../components/appointments/notifications';
import LoadingMask from '../../components/appointments/loadingMask';
import { userContext } from '../../ContextProvider';


const DeleteAccountPage = ({ accountFromUrl, acctId }) => {
  // acctId is now received as a prop
  const [storedAcctNo, setStoredAcctNo] = useState(''); // Store the actual acctNo from URL
  const [typedAcctNo, setTypedAcctNo] = useState(''); // User input for verification
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError, NotificationComponent } = useNotifications();
  const { logout, acctId: loggedInAcctId } = useContext(userContext);


  const token = localStorage.getItem('token');
  // Always get acctId from backend using accountFromUrl (acctNo from settings page)
  // Replace local getAcctIdFromBackend with shared module
  React.useEffect(() => {
    // Use acctId prop directly
    if (accountFromUrl) {
      if (acctId) {
        setStoredAcctNo(accountFromUrl); // Use the account number from URL for verification
      } else {
        setStoredAcctNo('');
        setTypedAcctNo('');
        setIsConfirmOpen(false);
        showError('Account ID not found for this account.');
      }
    }
    // eslint-disable-next-line
  }, [accountFromUrl, acctId]);
  
  const handleTypedAcctNoChange = (e) => {
    setTypedAcctNo(e.target.value);
  };
   // Helper for case-insensitive, trimmed account number match
  const isAccountNoMatch =
    typedAcctNo.trim().toLowerCase() === storedAcctNo.trim().toLowerCase();

  const handleDeleteRequest = () => {
    if (!acctId || !acctId.trim()) {
      showError('Please enter an Account ID');
      return;
    }

    if (!isAccountNoMatch) {
      showError('Account verification failed: The entered Account No. does not match your account');
      return;
    }

    setIsConfirmOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      if (!isAccountNoMatch) {
        showError('Account verification failed: The entered Account No. does not match your account');
        setIsConfirmOpen(false);
        return;
      }
      setIsDeleting(true);
      setIsLoading(true);
      const response = await fetch('/api/ui/appointment/gCal/deleteAccount', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`},
        body: JSON.stringify({ acctId }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      showSuccess('Account successfully deleted');
      setTypedAcctNo('');

      // After deletion, fetch latest accounts and set next available as default
      setIsLoading(true);
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setTimeout(async () => {
        try {
          const userId = localStorage.getItem('userId');
          // Fetch latest accounts for user
          const resp = await fetch(`/api/ui/appointment/gCal/accountName/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const result = await resp.json();
          let nextAcctNo = '';
          if (resp.ok && result.success && Array.isArray(result.accounts) && result.accounts.length > 0) {
            // Pick first available account
            nextAcctNo = result.accounts[0].acctNo || result.accounts[0].accountNo || '';
          }
          localStorage.setItem('acctNo', nextAcctNo);
          setIsLoading(false); // Hide loading mask after redirect
          if (nextAcctNo) {
            // Redirect to appointments with new account
            window.location.href = `/appointments?acc=${nextAcctNo}`;
          } else {
            // No accounts left, redirect to appointments (dashboard will show 'No Account Linked')
            window.location.href = '/appointments';
          }
        } catch {
          // On error, fallback to no account
          localStorage.setItem('acctNo', '');
          setIsLoading(false);
          window.location.href = '/appointments';
        }
      }, 1500);
    } catch (error) {
      showError('Failed to delete account: ' + (error.message || 'Unknown error'));
      setIsDeleting(false);
      setIsLoading(false);
      setIsConfirmOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmOpen(false);
  };

  return (
    <div className="container mx-auto px-4 py-6 relative">
      <NotificationComponent />
      {/* Full window loading mask overlay */}
      {isLoading && (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <LoadingMask loading={true} />
        </div>
      )}
      <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow">
        <h2 className="text-2xl font-bold mb-6 text-red-600">Delete Account</h2>
        <p className="mb-6 text-gray-700">
          This action cannot be undone. It will permanently delete your account and remove your data from our servers.
        </p>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Verification
          </label>
          <p className="mb-3 text-sm text-gray-600">
            To confirm deletion, please enter your Account No. below:
          </p>
          <input
            type="text"
            value={typedAcctNo}
            onChange={handleTypedAcctNoChange}
            className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black"
            placeholder="Enter your Account No"
          />
          {typedAcctNo && !isAccountNoMatch && (
            <p className="mt-1 text-sm text-red-600">
              Account No. does not match your account.
            </p>
          )}
          {typedAcctNo && isAccountNoMatch && (
            <>
              <p className="mt-1 text-sm text-green-600">
                Account No. verified.
              </p>
              
              <button
                onClick={handleDeleteRequest}
                disabled={isDeleting || !acctId.trim()}
                className={`
                  ${isDeleting || !acctId.trim()
                    ? 'bg-gray-300 cursor-not-allowed' 
                    : 'bg-red-600 hover:bg-red-700'}
                  text-white px-4 py-2 rounded w-full md:w-auto mt-2`
                }
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </>
          )}
        </div>
        
    {isConfirmOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Confirm Account Deletion</h3>
              <p className="mb-6">
                Are you sure you want to permanently delete Account No. <span className="font-semibold">{storedAcctNo}</span>?
              </p>
              <p className="mb-4 text-sm text-gray-500">
                Account ID: <span className="font-mono">{acctId}</span>
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 rounded text-white hover:bg-red-700"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountPage;
